import styles from 'components/Common/DropdownSelectCompact/DropdownSelectCompact.module.scss';
import OpButton from 'components/Common/op-ui-wrappers/OpButton/OpButton';
import OpDrawer from 'components/Common/op-ui-wrappers/OpDrawer/OpDrawer';
import OpRadio from 'components/Common/op-ui-wrappers/OpRadio/OpRadio';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import DesktopOnly from 'components/Common/Utils/DesktopOnly';
import MobileOnly from 'components/Common/Utils/MobileOnly';
import useClickOutside from 'hooks/useClickOutside';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import isDesktop from 'store/app/selector/isDesktop';

type OwnProps<T> = React.PropsWithChildren<{
  label: string;
  labelDecorator?: (text: string) => string;
  titleDecorator?: (text: string) => string;
  options: Array<{ title: string; value: T }>;
  selected?: T;
  onChange?: (value: T) => void;
  onClose?: () => void;
}>;

export default function DropdownSelectCompact<T>({
  label,
  labelDecorator,
  titleDecorator,
  onChange,
  onClose,
  options,
  selected,
}: OwnProps<T>) {
  const tt = titleDecorator ?? ((s) => s);
  const dropdownOptionsRef = useRef(null);
  const buttonRef = useRef(null);
  const isDesktopDevice = useSelector(isDesktop);
  const [isOpen, setIsOpen] = useState(false);

  const selectedTitle = (options.find((_) => _.value === selected) || options[0]).title;

  const toggle = () => setIsOpen(!isOpen);

  const handleChange = (value: T) => {
    setIsOpen(false);
    onChange && onChange(value);
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose && onClose();
  };

  useClickOutside(dropdownOptionsRef, handleClose, buttonRef);

  const renderOptions = options?.map((option) => (
    <OpRadio
      key={option.title + option.value}
      className={styles.dropdownOption}
      checked={selected === option.value ? true : undefined}
      onClick={() => handleChange(option.value)}
    >
      <OpText slot="heading" variant={isDesktopDevice ? 'body-medium-regular' : 'body-large-regular'}>
        {tt(option.title)}
      </OpText>
    </OpRadio>
  ));

  return (
    <div className={styles.container}>
      {isDesktopDevice && <OpText variant="body-medium-regular">{tt(label)}</OpText>}

      <OpButton ref={buttonRef} size="small" variant="secondary" onClick={toggle} icon-right="dropdown-arrow-down">
        {tt(selectedTitle)}
      </OpButton>

      <DesktopOnly and={isOpen}>
        <div ref={dropdownOptionsRef} className={styles.dropdown}>
          <div className={styles.dropdownInner}>{renderOptions}</div>
        </div>
      </DesktopOnly>

      <MobileOnly>
        <OpDrawer ref={dropdownOptionsRef} visible={isOpen} onClose={handleClose}>
          <OpText className={styles.drawerLabel} slot="label" variant="title-medium-bold">
            {(labelDecorator ?? ((s) => s))(label)}
          </OpText>

          <div slot="content" className={styles.drawerContent}>
            <div className={styles.drawerInner}>{renderOptions}</div>
          </div>
        </OpDrawer>
      </MobileOnly>
    </div>
  );
}
